import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '../utils/Api';
import { jwtDecode } from "jwt-decode";


const initialState = {
  user: {},
  userList: [],
  blockedUsers: [],
  username: "",
  email: "",
  isLoggedIn: false,
  error: null,
  token: localStorage.getItem("userToken") || null,
};

export const getUserList = createAsyncThunk(
  'userAuth/userList',
  async (_, thunkAPI) => {
    try {
      const res = await API.get("/users");
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
);

// Example async thunk for login
export const loginUser = createAsyncThunk(
  'userAuth/loginUser',
  async (credentials, thunkAPI) => {
    try {
      const res = await API.post("/users/login", credentials);
      localStorage.setItem("userToken", res.data.data.token);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
);

// Register 
export const registerUser = createAsyncThunk(
  'userAuth/registerUser',
  async (credentials, thunkAPI) => {
    // Replace with your API call
    try {
      const res = await API.post("/users/register", credentials);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
)
export const profileInfo = createAsyncThunk(
  'userAuth/profileInfo',
  async (_, thunkAPI) => {
    try {
      const res = await API.get("/users/me");
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
)

export const updateUser = createAsyncThunk(
  'userAuth/updateProfile',
  async (profileData, thunkAPI) => {
    try {
      let payload = profileData;
      let config = {};

      if (profileData instanceof FormData) {
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      } else if (profileData?.avatarFile) {
        const formData = new FormData();
        Object.entries(profileData).forEach(([key, value]) => {
          if (key === 'avatarFile') return;
          if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
        formData.append('avatar', profileData.avatarFile);
        payload = formData;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      }

      const res = await API.put("/users", payload, config);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
);

export const fetchBlockedUsers = createAsyncThunk(
  'userAuth/blockedList',
  async (_, thunkAPI) => {
    try {
      const res = await API.get('/users/blocked/list');
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
);

export const blockUser = createAsyncThunk(
  'userAuth/blockUser',
  async (userId, thunkAPI) => {
    try {
      const res = await API.post(`/users/block/${userId}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
);

export const unblockUser = createAsyncThunk(
  'userAuth/unblockUser',
  async (userId, thunkAPI) => {
    try {
      const res = await API.delete(`/users/block/${userId}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.error);
    }
  }
);


const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    login: (state, action) => {
      state.username = action.payload.username;
      state.email = action.payload.email;
      state.isLoggedIn = true;
    },
    logout: (state) => {
      state.username = "";
      state.email = "";
      state.isLoggedIn = false;
      state.token = null;
      localStorage.removeItem("userToken");
    },

    checkToken: (state) => {
      try {
        if (state.token && state.token.split('.').length === 3) {
          const decoded = jwtDecode(state.token);
          if (decoded.exp * 1000 < Date.now()) {
            state.user = null;
            state.token = null;
            localStorage.removeItem("userToken");
          } else {
            state.user = decoded;
          }
        } else {
          state.user = null;
          state.token = null;
          localStorage.removeItem("userToken");
        }
      } catch {
        state.user = null;
        state.token = null;
        localStorage.removeItem("userToken");
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.username = "";
        state.email = "";
        state.isLoggedIn = false;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.username = action.payload.data.user.username;
        state.email = action.payload.data.user.email;
        state.isLoggedIn = true;
        state.token = action.payload.data.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.username = "";
        state.email = "";
        state.error = action.payload;
        state.isLoggedIn = false;
      })

      .addCase(registerUser.pending, (state) => {
        state.username = "";
        state.email = "";
        state.isLoggedIn = false;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.username = action.payload.data.username;
        state.email = action.payload.data.email;
        state.isLoggedIn = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.username = "";
        state.email = "";
        state.error = action.payload;
        state.isLoggedIn = false;
      })
      .addCase(profileInfo.pending, (state) => {
        state.user = {};
      })
      .addCase(profileInfo.fulfilled, (state, action) => {
        state.user = action.payload.data;
      })
      .addCase(profileInfo.rejected, (state, action) => {
        state.user = {};
        state.error = action.payload;
      })
      .addCase(updateUser.pending, (state) => {
        state.user = {};
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.user = action.payload.data;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.user = {};
        state.error = action.payload;
      })
      .addCase(getUserList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserList.fulfilled, (state, action) => {
        state.loading = false;
        state.userList = action.payload.data;
        console.log("Fetched User List:", state.userList);
      })
      .addCase(getUserList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

      builder
      .addCase(fetchBlockedUsers.fulfilled, (state, action) => {
        state.blockedUsers = action.payload.data || [];
      })
      .addCase(blockUser.fulfilled, (state, action) => {
        state.blockedUsers = action.payload.data || [];
      })
      .addCase(unblockUser.fulfilled, (state, action) => {
        state.blockedUsers = action.payload.data || [];
      });

  },
});

export const { login, logout, checkToken } = userSlice.actions;

export default userSlice.reducer;
